// ============================================================================
// src/services/marketplaceService.ts - FIXED FOR APICLIENT ARCHITECTURE
// Compatible với apiClient.ts từ project knowledge
// ============================================================================

import apiClient from '@/api/apiClient';
import {
  SearchContentRequest,
  SearchContentResponse,
  SmartSearchSuggestion,
  ContentRecommendation,
  MarketplaceStats,
  MarketplaceFilters,
  LicenseOption,
  LicenseOptionFilters,
  CreateLicenseOptionData,
  UpdateLicenseOptionData,
  LicensePurchase,
  LicensePurchaseData,
  PublicCatalog
} from '@/types/marketplace';
import { StrapiResponse, OperationResult, ID } from '@/types/common';

/**
 * Marketplace Service - FIXED
 * Sử dụng đúng apiClient architecture:
 * - apiClient.get/post/put/delete cho raw HTTP
 * - apiClient.createRecord/updateRecordById/deleteRecordById cho CRUD
 */

interface ContractData {
  contractNumber: string;
  signDate: string;
  partyA: {
    fullname: string;
    idNumber?: string;
    address?: string;
    phone?: string;
    email?: string;
    position?: string;
  };
  worksAppendix: Array<{
    title: string;
    author: string;
    year: number;
    note?: string;
  }>;
  territory: string;
  validityYears: number;
  commissionRate: number;
  signatureDataUrl: string; // Base64 PNG image
}
class MarketplaceService {

  // ========================================
  // POPULATE HELPERS
  // ========================================

  private buildPopulateParams(deep: boolean = true): Record<string, string> {
    if (deep) {
      return {
        // Populate toàn bộ, không restrict fields
        'populate[content]': 'true',
        'populate[owner]': 'true',
        'populate[license_options][populate][license_template]': 'true'
      };
    } else {
      return {
        'populate[content]': 'true',
        'populate[license_options]': 'true'
      };
    }
  }

  private buildFilterParams(filters: MarketplaceFilters = {}): Record<string, string> {
    const params: Record<string, string> = {};
    params['filters[isVisible][$eq]'] = 'true';

    if (filters.categories?.length) {
      filters.categories.forEach((cat, i) => {
        params[`filters[content][category][slug][$in][${i}]`] = cat;
      });
    }

    if (filters.contentTypes?.length) {
      filters.contentTypes.forEach((type, i) => {
        params[`filters[content][contentKind][$in][${i}]`] = type;
      });
    }

    if (filters.licenseTypes?.length) {
      filters.licenseTypes.forEach((type, i) => {
        params[`filters[license_options][license_template][category][$in][${i}]`] = type;
      });
    }

    if (filters.priceRange?.min) {
      params['filters[license_options][price][$gte]'] = filters.priceRange.min.toString();
    }
    if (filters.priceRange?.max) {
      params['filters[license_options][price][$lte]'] = filters.priceRange.max.toString();
    }

    if (filters.tags?.length) {
      filters.tags.forEach((tag, i) => {
        params[`filters[tags][$containsi][${i}]`] = tag;
      });
    }

    if (filters.searchQuery?.trim()) {
      const query = filters.searchQuery.trim();
      params['filters[$or][0][content][title][$containsi]'] = query;
      params['filters[$or][1][shortDescription][$containsi]'] = query;
      params['filters[$or][2][tags][$containsi]'] = query;
    }

    return params;
  }

  private buildSortParams(sortBy: string = 'newest'): string {
    const sortMap: Record<string, string> = {
      relevance: 'viewCount:desc',
      newest: 'createdAt:desc',
      oldest: 'createdAt:asc',
      price_low: 'license_options.price:asc',
      price_high: 'license_options.price:desc',
      popular: 'viewCount:desc',
      rating: 'marketplace_reviews.rating:desc'
    };
    return sortMap[sortBy] || 'createdAt:desc';
  }

  // ========================================
  // SEARCH & DISCOVERY - FIXED
  // ========================================

  async searchContents(request: SearchContentRequest): Promise<SearchContentResponse> {
    try {
      const params = {
        ...this.buildPopulateParams(true),
        ...this.buildFilterParams(request.filters),
        'pagination[page]': request.page?.toString() || '1',
        'pagination[pageSize]': request.limit?.toString() || '20',
        'sort': this.buildSortParams(request.filters?.sortBy)
      };

      if (request.query?.trim()) {
        params['filters[$or][0][content][title][$containsi]'] = request.query;
        params['filters[$or][1][shortDescription][$containsi]'] = request.query;
      }

      const response = await apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', { params });

      return {
        data: response.data.data || [],
        meta: {
          pagination: response.data.meta?.pagination || {
            page: 1,
            pageSize: 20,
            pageCount: 1,
            total: 0
          }
        }
      };
    } catch (error) {
      console.error('Search contents error:', error);
      throw error;
    }
  }

  async getFeaturedContents(limit: number = 10): Promise<PublicCatalog[]> {
    try {
      const params = {
        ...this.buildPopulateParams(true),
        'filters[isVisible][$eq]': 'true',
        'filters[highlightOrder][$notNull]': 'true',
        'sort': 'highlightOrder:asc',
        'pagination[pageSize]': limit.toString()
      };

      const response = await apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Get featured error:', error);
      return [];
    }
  }

  async getTrendingContents(limit: number = 10): Promise<PublicCatalog[]> {
    try {
      const params = {
        ...this.buildPopulateParams(true),
        'filters[isVisible][$eq]': 'true',
        'sort': 'viewCount:desc',
        'pagination[pageSize]': limit.toString()
      };

      const response = await apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Get trending error:', error);
      return [];
    }
  }

  async getNewContents(limit: number = 10): Promise<PublicCatalog[]> {
    try {
      const params = {
        ...this.buildPopulateParams(true),
        'filters[isVisible][$eq]': 'true',
        'sort': 'createdAt:desc',
        'pagination[pageSize]': limit.toString()
      };

      const response = await apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Get new contents error:', error);
      return [];
    }
  }

  async getContentById(catalogId: ID): Promise<PublicCatalog> {
    try {
      const params = this.buildPopulateParams(true);
      const response = await apiClient.get<StrapiResponse<PublicCatalog>>(`/api/public-catalogs/${catalogId}`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Get content by ID error:', error);
      throw error;
    }
  }

  async getContentsByCategory(categorySlug: string, page: number = 1, pageSize: number = 20): Promise<PublicCatalog[]> {
    try {
      const params = {
        ...this.buildPopulateParams(true),
        'filters[isVisible][$eq]': 'true',
        'filters[content][category][slug][$eq]': categorySlug,
        'sort': 'createdAt:desc',
        'pagination[page]': page.toString(),
        'pagination[pageSize]': pageSize.toString()
      };

      const response = await apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Get by category error:', error);
      return [];
    }
  }

  async getDiscountedContents(limit: number = 10): Promise<PublicCatalog[]> {
    try {
      const params = {
        ...this.buildPopulateParams(true),
        'filters[isVisible][$eq]': 'true',
        'filters[license_options][discountPercentage][$gt]': '0',
        'sort': 'license_options.discountPercentage:desc',
        'pagination[pageSize]': limit.toString()
      };

      const response = await apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Get discounted error:', error);
      return [];
    }
  }

  async getContentsByLicenseType(
    licenseType: 'personal' | 'commercial' | 'extended' | 'exclusive' | 'editorial' | 'royalty_free',
    limit: number = 20
  ): Promise<PublicCatalog[]> {
    try {
      const params = {
        ...this.buildPopulateParams(true),
        'filters[isVisible][$eq]': 'true',
        'filters[license_options][license_template][category][$eq]': licenseType,
        'pagination[pageSize]': limit.toString()
      };

      const response = await apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Get by license type error:', error);
      return [];
    }
  }

  // ========================================
  // LICENSE OPTIONS - FIXED WITH apiClient.createRecord
  // ========================================

  async getLicenseOptions(filters?: LicenseOptionFilters): Promise<LicenseOption[]> {
    try {
      const params: Record<string, string> = {
        'populate[license_template][fields][0]': 'name',
        'populate[license_template][fields][1]': 'category',
        'sort': 'sortOrder:asc'
      };

      if (filters?.public_catalog) {
        params['filters[public_catalog][documentId][$eq]'] = filters.public_catalog.toString();
      }
      if (filters?.isActive !== undefined) {
        params['filters[isActive][$eq]'] = filters.isActive.toString();
      }
      if (filters?.priceMin) {
        params['filters[price][$gte]'] = filters.priceMin.toString();
      }
      if (filters?.priceMax) {
        params['filters[price][$lte]'] = filters.priceMax.toString();
      }

      const response = await apiClient.get<StrapiResponse<LicenseOption[]>>('/api/license-options', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Get license options error:', error);
      return [];
    }
  }

  async getLicenseOptionById(optionId: ID): Promise<LicenseOption> {
    try {
      const params = {
        'populate[license_template]': '*',
        'populate[public_catalog][populate][content]': '*'
      };

      const response = await apiClient.get<StrapiResponse<LicenseOption>>(`/api/license-options/${optionId}`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Get license option error:', error);
      throw error;
    }
  }

  async createLicenseOption(data: CreateLicenseOptionData): Promise<OperationResult<LicenseOption>> {
    try {
      const result = await apiClient.createRecord<LicenseOption>('license-option', {
        public_catalog: data.public_catalog,
        name: data.name,
        displayName: data.displayName,
        description: data.description,
        price: data.price,
        currency: data.currency,
        duration: data.duration,
        usageRights: data.usageRights,
        restrictions: data.restrictions,
        isActive: data.isActive,
        sortOrder: data.sortOrder,
        ...(data.license_template && { license_template: data.license_template })
      });

      return result;
    } catch (error) {
      console.error('Create license option error:', error);
      throw error;
    }
  }

  async updateLicenseOption(optionId: ID, data: UpdateLicenseOptionData): Promise<OperationResult<LicenseOption>> {
    try {
      const result = await apiClient.updateRecordById<LicenseOption>('license-option', optionId, data);
      return result;
    } catch (error) {
      console.error('Update license option error:', error);
      throw error;
    }
  }

  async deleteLicenseOption(optionId: ID): Promise<OperationResult<LicenseOption>> {
    try {
      const result = await apiClient.deleteRecordById<LicenseOption>('license-option', optionId);
      return result;
    } catch (error) {
      console.error('Delete license option error:', error);
      throw error;
    }
  }

  // ========================================
  // PURCHASE - FIXED WITH apiClient.createRecord
  // ========================================

  async purchaseLicense(purchaseData: LicensePurchaseData): Promise<LicensePurchase> {
    try {
      const purchaseNumber = `PUR-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      const licenseKey = `LIC-${Date.now()}-${Math.random().toString(36).substr(2, 16).toUpperCase()}`;

      const option = await this.getLicenseOptionById(purchaseData.license_option);
      const quantity = purchaseData.quantity || 1;
      const totalPrice = option.price * quantity;

      const result = await apiClient.createRecord<LicensePurchase>('license-purchase', {
        license_option: purchaseData.license_option,
        buyer: purchaseData.buyer,
        purchaseNumber,
        licenseKey,
        purchaseDate: new Date().toISOString(),
        quantity,
        unitPrice: option.price,
        totalPrice,
        currency: option.currency,
        paymentStatus: 'pending',
        licenseStatus: 'active',
        startDate: new Date().toISOString(),
        ...(option.duration && {
          endDate: new Date(Date.now() + option.duration * 24 * 60 * 60 * 1000).toISOString()
        }),
        downloadCount: 0,
        usageCount: 0,
        purchaseMetadata: purchaseData.metadata
      });

      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create purchase');
      }

      return result.data!;
    } catch (error) {
      console.error('Purchase license error:', error);
      throw error;
    }
  }

  async getUserPurchases(userId: ID): Promise<LicensePurchase[]> {
    try {
      const params = {
        'filters[buyer][documentId][$eq]': userId.toString(),
        'populate[license_option][populate][public_catalog][populate][content]': '*',
        'populate[license_option][populate][license_template]': '*',
        'sort': 'purchaseDate:desc'
      };

      const response = await apiClient.get<StrapiResponse<LicensePurchase[]>>('/api/license-purchases', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Get user purchases error:', error);
      return [];
    }
  }

  async getPurchaseById(purchaseId: ID): Promise<LicensePurchase> {
    try {
      const params = {
        'populate[license_option][populate][public_catalog][populate][content]': '*',
        'populate[buyer]': '*',
        'populate[seller]': '*'
      };

      const response = await apiClient.get<StrapiResponse<LicensePurchase>>(`/api/license-purchases/${purchaseId}`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Get purchase error:', error);
      throw error;
    }
  }

  async updatePurchaseStatus(purchaseId: ID, status: 'pending' | 'completed' | 'failed' | 'refunded'): Promise<OperationResult<LicensePurchase>> {
    try {
      const result = await apiClient.updateRecordById<LicensePurchase>('license-purchase', purchaseId, {
        paymentStatus: status
      });
      return result;
    } catch (error) {
      console.error('Update purchase status error:', error);
      throw error;
    }
  }

  // ========================================
  // SEARCH SUGGESTIONS
  // ========================================

  async getSearchSuggestions(query: string): Promise<SmartSearchSuggestion[]> {
    try {
      const [contentResults, tagResults] = await Promise.all([
        apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', {
          params: {
            'filters[isVisible][$eq]': 'true',
            'filters[$or][0][content][title][$containsi]': query,
            'filters[$or][1][shortDescription][$containsi]': query,
            'pagination[pageSize]': '5',
            'populate[content][fields][0]': 'title'
          }
        }),
        apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', {
          params: {
            'filters[isVisible][$eq]': 'true',
            'filters[tags][$containsi]': query,
            'pagination[pageSize]': '5'
          }
        })
      ]);

      const suggestions: SmartSearchSuggestion[] = [];

      contentResults.data.data?.forEach(catalog => {
        if (catalog.content?.title) {
          suggestions.push({
            type: 'content',
            text: catalog.content.title,
            relevance: 1,
            metadata: { catalogId: catalog.documentId }
          });
        }
      });

      tagResults.data.data?.forEach(catalog => {
        catalog.tags?.forEach(tag => {
          if (tag.toLowerCase().includes(query.toLowerCase())) {
            if (!suggestions.find(s => s.text === tag)) {
              suggestions.push({ type: 'tag', text: tag, relevance: 0.8 });
            }
          }
        });
      });

      return suggestions.slice(0, 10);
    } catch (error) {
      console.error('Get suggestions error:', error);
      return [];
    }
  }

  async getRecommendations(userId?: ID): Promise<ContentRecommendation[]> {
    try {
      const [popular, trending, newItems] = await Promise.all([
        this.getFeaturedContents(3),
        this.getTrendingContents(3),
        this.getNewContents(3)
      ]);

      const recommendations: ContentRecommendation[] = [];

      popular.forEach(catalog => {
        recommendations.push({ content: catalog, score: 0.9, reason: 'popular' });
      });

      trending.forEach(catalog => {
        if (!recommendations.find(r => r.content.documentId === catalog.documentId)) {
          recommendations.push({ content: catalog, score: 0.85, reason: 'trending' });
        }
      });

      newItems.forEach(catalog => {
        if (!recommendations.find(r => r.content.documentId === catalog.documentId)) {
          recommendations.push({ content: catalog, score: 0.7, reason: 'similar' });
        }
      });

      return recommendations.sort((a, b) => b.score - a.score).slice(0, 10);
    } catch (error) {
      console.error('Get recommendations error:', error);
      return [];
    }
  }

  // ========================================
  // COLLECTIONS
  // ========================================

  async getCollection(collectionId: ID): Promise<PublicCatalog[]> {
    try {
      const params = {
        ...this.buildPopulateParams(true),
        'filters[marketplace_collections][documentId][$eq]': collectionId.toString()
      };

      const response = await apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Get collection error:', error);
      return [];
    }
  }

  // ========================================
  // TRACKING - FIXED WITH apiClient.createRecord
  // ========================================

  async trackContentView(catalogId: ID): Promise<void> {
    try {
      await apiClient.createRecord('marketplace-analytic', {
        public_catalog: catalogId,
        eventType: 'view',
        timestamp: new Date().toISOString()
      });

      const catalog = await this.getContentById(catalogId);
      await apiClient.updateRecordById('public-catalog', catalogId, {
        viewCount: (catalog.viewCount || 0) + 1
      });
    } catch (error) {
      console.error('Track view error:', error);
    }
  }

  async trackContentDownload(catalogId: ID): Promise<void> {
    try {
      await apiClient.createRecord('marketplace-analytic', {
        public_catalog: catalogId,
        eventType: 'download',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Track download error:', error);
    }
  }

  async trackContentShare(catalogId: ID, platform: string): Promise<void> {
    try {
      await apiClient.createRecord('marketplace-analytic', {
        public_catalog: catalogId,
        eventType: 'share',
        timestamp: new Date().toISOString(),
        metadata: { platform }
      });
    } catch (error) {
      console.error('Track share error:', error);
    }
  }

  async trackContentLike(catalogId: ID, userId: ID, likeType: string): Promise<void> {
    try {
      await apiClient.createRecord('marketplace-analytic', {
        public_catalog: catalogId,
        user: userId,
        eventType: 'bookmark',
        timestamp: new Date().toISOString(),
        metadata: { likeType }
      });
    } catch (error) {
      console.error('Track like error:', error);
    }
  }

  async trackContentRating(catalogId: ID, userId: ID, rating: number): Promise<void> {
    try {
      await apiClient.createRecord('marketplace-analytic', {
        public_catalog: catalogId,
        user: userId,
        eventType: 'rating',
        timestamp: new Date().toISOString(),
        value: rating
      });
    } catch (error) {
      console.error('Track rating error:', error);
    }
  }

  async getContentAnalytics(catalogId: ID, period: 'week' | 'month' | 'year' = 'month'): Promise<any> {
    try {
      const dateFrom = new Date();
      switch (period) {
        case 'week': dateFrom.setDate(dateFrom.getDate() - 7); break;
        case 'month': dateFrom.setMonth(dateFrom.getMonth() - 1); break;
        case 'year': dateFrom.setFullYear(dateFrom.getFullYear() - 1); break;
      }

      const response = await apiClient.get('/api/marketplace-analytics', {
        params: {
          'filters[public_catalog][documentId][$eq]': catalogId.toString(),
          'filters[timestamp][$gte]': dateFrom.toISOString()
        }
      });

      const analytics = response.data.data || [];
      const eventCounts: Record<string, number> = {};
      analytics.forEach((event: any) => {
        eventCounts[event.eventType] = (eventCounts[event.eventType] || 0) + 1;
      });

      return {
        period,
        totalEvents: analytics.length,
        eventCounts,
        views: eventCounts.view || 0,
        downloads: eventCounts.download || 0
      };
    } catch (error) {
      console.error('Get analytics error:', error);
      throw error;
    }
  }

  // ========================================
  // STATISTICS
  // ========================================

  async getMarketplaceStats(): Promise<MarketplaceStats> {
    try {
      const [catalogsResponse, analyticsResponse] = await Promise.all([
        apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', {
          params: { 'filters[isVisible][$eq]': 'true', 'pagination[pageSize]': '1' }
        }),
        apiClient.get('/api/marketplace-analytics', {
          params: { 'pagination[pageSize]': '1' }
        })
      ]);

      return {
        totalContents: catalogsResponse.data.meta?.pagination?.total || 0,
        totalCreators: 0,
        totalLicenses: 0,
        totalRevenue: 0,
        topCategories: [],
        recentActivity: []
      };
    } catch (error) {
      console.error('Get stats error:', error);
      throw error;
    }
  }

  async getCategoryStats(): Promise<Array<{ name: string; count: number }>> {
    try {
      const response = await apiClient.get('/api/marketplace-categories', {
        params: { 'pagination[pageSize]': '100' }
      });

      return (response.data.data || []).map((cat: any) => ({
        name: cat.name,
        count: cat.contentCount || 0
      })).sort((a: any, b: any) => b.count - a.count);
    } catch (error) {
      console.error('Get category stats error:', error);
      return [];
    }
  }

  async getTrendingTags(limit: number = 20): Promise<string[]> {
    try {
      const response = await apiClient.get<StrapiResponse<PublicCatalog[]>>('/api/public-catalogs', {
        params: { 'filters[isVisible][$eq]': 'true', 'pagination[pageSize]': '100' }
      });

      const tagCounts: Record<string, number> = {};
      (response.data.data || []).forEach((catalog: PublicCatalog) => {
        catalog.tags?.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      });

      return Object.entries(tagCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([tag]) => tag);
    } catch (error) {
      console.error('Get trending tags error:', error);
      return [];
    }
  }

  // ========================================
  // PUBLISHING - FIXED WITH apiClient.createRecord
  // ========================================

  async createPublicCatalog(data: {
    content: ID;
    owner: ID;
    shortDescription: string;
    tags: string[];
    isVisible?: boolean;
    highlightOrder?: number | null;
    // ✅ NEW: Contract fields
    contractData?: ContractData;
    contractHtml?: string;
  }): Promise<OperationResult<PublicCatalog>> {
    try {
      const result = await apiClient.createRecord<PublicCatalog>('public-catalog', {
        content: data.content as any,
        owner: data.owner as any,
        shortDescription: data.shortDescription,
        tags: data.tags || [],
        isVisible: data.isVisible !== false,
        highlightOrder: data.highlightOrder || null,
        viewCount: 0,
        
        // ✅ NEW: Include contract data
        contractData: data.contractData ? JSON.stringify(data.contractData) : null,
        contractHtml: data.contractHtml || null,
        contractPdfUrl: null, // Will be generated by backend
        contractSignedAt: data.contractData ? new Date().toISOString() : null
      });

      return result;
    } catch (error: any) {
      console.error('Create catalog error:', error);
      return {
        success: false,
        data: null,
        error: { message: error.message || 'Failed to create catalog' }
      };
    }
  }

  async updateContent(contentId: ID, data: any): Promise<OperationResult<any>> {
    try {
      const result = await apiClient.updateRecordById('content', contentId, data);
      return result;
    } catch (error: any) {
      console.error('Update content error:', error);
      return {
        success: false,
        data: null,
        error: { message: error.message }
      };
    }
  }

  async publishContentToMarketplace(contentId: ID, data: any): Promise<OperationResult<PublicCatalog>> {
    try {
      console.log('📤 Publishing to marketplace with data:', {
        contentId,
        hasContractData: !!data.contractData,
        hasSignature: !!data.contractData?.signatureDataUrl
      });

      // Create catalog with contract data
      const catalogResult = await this.createPublicCatalog({
        content: contentId,
        owner: data.owner,
        shortDescription: data.shortDescription,
        tags: data.tags,
        isVisible: data.isVisible,
        highlightOrder: data.highlightOrder,
        
        // ✅ NEW: Pass contract data
        contractData: data.contractData,
        contractHtml: data.contractHtml
      });

      if (!catalogResult.success || !catalogResult.data) {
        throw new Error(catalogResult.error?.message || 'Failed to create catalog');
      }

      const catalogId = catalogResult.data.documentId;

      // Create license options
      if (data.licenseOptions && data.licenseOptions.length > 0) {
        console.log(`📋 Creating ${data.licenseOptions.length} license options...`);
        
        for (const option of data.licenseOptions) {
          await this.createLicenseOption({
            public_catalog: catalogId,
            ...option,
            isActive: true
          });
        }
      }

      // TODO - Có cần thiết update không - dữ liệu đã có



      // // Link catalog to content
      // await this.updateContent(contentId, {
      //   publicCatalog: catalogId
      // });

      // // ✅ NEW: Trigger backend to generate PDF (if contract data exists)
      // if (data.contractData) {
      //   console.log('🔄 Triggering PDF generation...');
      //   try {
      //     await this.generateContractPdf(catalogId, data.contractData);
      //   } catch (pdfError) {
      //     console.error('⚠️ PDF generation failed (non-blocking):', pdfError);
      //     // Don't fail the entire publish process if PDF generation fails
      //   }
      // }

      console.log('✅ Successfully published to marketplace:', catalogId);
      return catalogResult;

    } catch (error: any) {
      console.error('❌ Publish to marketplace error:', error);
      return {
        success: false,
        data: null,
        error: { message: error.message || 'Failed to publish' }
      };
    }
  }

  // ✅ NEW: Generate Contract PDF
  async generateContractPdf(catalogId: ID, contractData: ContractData): Promise<OperationResult<any>> {
    try {
      const response = await apiClient.post('/api/contracts/generate-pdf', {
        catalogId,
        contractData
      });

      console.log('✅ PDF generated successfully:', response.data);
      
      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error: any) {
      console.error('❌ PDF generation error:', error);
      return {
        success: false,
        data: null,
        error: { message: error.message || 'Failed to generate PDF' }
      };
    }
  }

  async updateMarketplaceContent(catalogId: ID, data: any): Promise<OperationResult<PublicCatalog>> {
    try {
      const result = await apiClient.updateRecordById<PublicCatalog>('public-catalog', catalogId, data);
      return result;
    } catch (error) {
      console.error('Update marketplace content error:', error);
      throw error;
    }
  }

  async removeFromMarketplace(catalogId: ID): Promise<OperationResult<PublicCatalog>> {
    try {
      const result = await apiClient.updateRecordById<PublicCatalog>('public-catalog', catalogId, {
        isVisible: false
      });
      return result;
    } catch (error) {
      console.error('Remove from marketplace error:', error);
      throw error;
    }
  }

  async updateContentVisibility(catalogId: ID, visible: boolean): Promise<OperationResult<PublicCatalog>> {
    try {
      const result = await apiClient.updateRecordById<PublicCatalog>('public-catalog', catalogId, {
        isVisible: visible
      });
      return result;
    } catch (error) {
      console.error('Update visibility error:', error);
      throw error;
    }
  }

  async getOwnerPublishedCatalogs(ownerId: ID): Promise<OperationResult<PublicCatalog[]>> {
    try {
      const response = await apiClient.get<PaginatedResponse<PublicCatalog>>('/api/public-catalogs', {
        params: {
          'filters[owner][documentId][$eq]': ownerId,
          'populate[content][populate]': '*',
          'populate[licenseOptions][populate]': '*',
          'sort': 'createdAt:desc',
          'pagination[pageSize]': 100
        }
      });

      return {
        success: true,
        data: response.data.data || [],
        error: null
      };
    } catch (error: any) {
      console.error('Get owner published catalogs error:', error);
      return {
        success: false,
        data: [],
        error: { message: error.message }
      };
    }
  }
  // ========================================
  // UTILITIES
  // ========================================

  async checkApiHealth(): Promise<boolean> {
    try {
      await apiClient.get('/api/public-catalogs', {
        params: { 'pagination[pageSize]': '1' }
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  async testConnectivity(): Promise<{
    basicConnection: boolean;
    publicCatalogs: boolean;
    licenseOptions: boolean;
    analytics: boolean;
  }> {
    const results = {
      basicConnection: false,
      publicCatalogs: false,
      licenseOptions: false,
      analytics: false
    };

    try {
      await apiClient.get('/api/public-catalogs', {
        params: { 'pagination[pageSize]': '1' }
      });
      results.publicCatalogs = true;
      results.basicConnection = true;
    } catch (error) {
      console.error('Public catalogs test failed');
    }

    try {
      await apiClient.get('/api/license-options', {
        params: { 'pagination[pageSize]': '1' }
      });
      results.licenseOptions = true;
    } catch (error) {
      console.error('License options test failed');
    }

    try {
      await apiClient.get('/api/marketplace-analytics', {
        params: { 'pagination[pageSize]': '1' }
      });
      results.analytics = true;
    } catch (error) {
      console.error('Analytics test failed');
    }

    return results;
  }

  async getHealthStatus(): Promise<{
    healthy: boolean;
    latency: number;
    features: string[];
  }> {
    const startTime = Date.now();

    try {
      const connectivity = await this.testConnectivity();
      const latency = Date.now() - startTime;

      const features = Object.entries(connectivity)
        .filter(([_, working]) => working)
        .map(([feature, _]) => feature);

      return {
        healthy: connectivity.basicConnection,
        latency,
        features
      };
    } catch (error) {
      return {
        healthy: false,
        latency: Date.now() - startTime,
        features: []
      };
    }
  }
}

export const marketplaceService = new MarketplaceService();
export default marketplaceService;